import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getRecordingByID } from "../services/recording";
import { Recording } from "../model/recording";

type SearchMode = "local" | "fetch";

interface UseSharedRecordingQueryOptions {
  matchRecording?: (recording: Recording) => boolean;
  onLoadError?: (error: unknown) => void;
}

export const useSharedRecordingQuery = ({
  matchRecording,
  onLoadError,
}: UseSharedRecordingQueryOptions = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const recordingIDQuery = (searchParams.get("q") ?? "").trim();
  const [searchQuery, setSearchQueryState] = useState("");
  const [sharedRecording, setSharedRecording] = useState<Recording | null>(
    null,
  );
  const [isLoadingSharedRecording, setIsLoadingSharedRecording] =
    useState(false);
  const [isPrefilledSharedQuery, setIsPrefilledSharedQuery] = useState(false);

  // Use refs so inline callbacks don't cause the effect to re-fire on every render
  const matchRecordingRef = useRef(matchRecording);
  const onLoadErrorRef = useRef(onLoadError);
  useEffect(() => {
    matchRecordingRef.current = matchRecording;
  }, [matchRecording]);
  useEffect(() => {
    onLoadErrorRef.current = onLoadError;
  }, [onLoadError]);

  // Functional update form so clearQueryParam doesn't capture searchParams
  const clearQueryParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        if (!prev.get("q")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("q");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handleSearchChange = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      setSearchQueryState(value);

      if (!trimmedValue) {
        setSharedRecording(null);
        setIsPrefilledSharedQuery(false);
        setIsLoadingSharedRecording(false);
        clearQueryParam();
        return;
      }

      if (
        sharedRecording &&
        trimmedValue.toLowerCase() !==
          sharedRecording.title.trim().toLowerCase()
      ) {
        setSharedRecording(null);
        setIsPrefilledSharedQuery(false);
        clearQueryParam();
      }
    },
    [clearQueryParam, sharedRecording],
  );

  const loadRecordingByID = useCallback(
    async (recordingID: string): Promise<void> => {
      setIsLoadingSharedRecording(true);
      try {
        const found = await getRecordingByID(recordingID);

        if (
          !found ||
          (matchRecordingRef.current && !matchRecordingRef.current(found))
        ) {
          setSharedRecording(null);
          setIsPrefilledSharedQuery(false);
          setSearchQueryState("");
          return;
        }

        setSharedRecording(found);
        setSearchQueryState(found.title);
        setIsPrefilledSharedQuery(true);
      } catch (error) {
        setSharedRecording(null);
        setIsPrefilledSharedQuery(false);
        setSearchQueryState("");
        onLoadErrorRef.current?.(error);
      } finally {
        setIsLoadingSharedRecording(false);
      }
    },
    [],
  );

  // Called when a typeahead suggestion is selected
  const selectSharedRecording = useCallback(
    async (recordingID: string): Promise<void> => {
      await loadRecordingByID(recordingID);
    },
    [loadRecordingByID],
  );

  useEffect(() => {
    if (!recordingIDQuery) {
      setSharedRecording(null);
      setIsPrefilledSharedQuery(false);
      setIsLoadingSharedRecording(false);
      return;
    }

    let isMounted = true;

    const load = async () => {
      setIsLoadingSharedRecording(true);
      try {
        const found = await getRecordingByID(recordingIDQuery);

        if (!isMounted) return;

        if (
          !found ||
          (matchRecordingRef.current && !matchRecordingRef.current(found))
        ) {
          setSharedRecording(null);
          setIsPrefilledSharedQuery(false);
          setSearchQueryState("");
          clearQueryParam();
          return;
        }

        setSharedRecording(found);
        setSearchQueryState(found.title);
        setIsPrefilledSharedQuery(true);
      } catch (error) {
        if (!isMounted) return;

        setSharedRecording(null);
        setIsPrefilledSharedQuery(false);
        setSearchQueryState("");
        clearQueryParam();
        onLoadErrorRef.current?.(error);
      } finally {
        if (isMounted) {
          setIsLoadingSharedRecording(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
    // matchRecording and onLoadError intentionally excluded — accessed via refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearQueryParam, recordingIDQuery]);

  const searchMode: SearchMode = isPrefilledSharedQuery ? "local" : "fetch";

  return {
    searchMode,
    searchQuery,
    setSearchQuery: handleSearchChange,
    sharedRecording,
    isLoadingSharedRecording,
    selectSharedRecording,
  };
};
